// ============================================================================
// COVER STRIP — per half-day, can a studio actually be booked?
// Bookable = room free (portal + direct-Outlook) AND a Tier 1 facilitator free.
// No cover = room free but no facilitator: the case the calendar used to hide.
// Booked rooms show nothing; their booking chip already says so.
// ============================================================================

import React from 'react';
import { ExternalBooking, HalfDay, SlotSummary } from '../../services/studio-bookings';
import { RoomWithAvailability } from '../../services/rooms';

const HALF_WINDOWS: Record<HalfDay, [string, string]> = {
  morning: ['09:00', '12:30'],
  afternoon: ['13:30', '17:00']
};
const HALF_LABEL: Record<HalfDay, string> = { morning: 'AM', afternoon: 'PM' };

export const NO_COVER_STYLE: React.CSSProperties = {
  backgroundImage: 'repeating-linear-gradient(135deg, rgba(239,68,68,0.14) 0 4px, transparent 4px 8px)'
};

function externallyBooked(externals: ExternalBooking[], roomId: string, date: string, half: HalfDay): boolean {
  const [from, to] = HALF_WINDOWS[half];
  const start = `${date}T${from}`;
  const end = `${date}T${to}`;
  return externals.some(e => e.roomId === roomId && e.startDateTime.slice(0, 16) < end && e.endDateTime.slice(0, 16) > start);
}

interface HalfState {
  bookable: { room: RoomWithAvailability; slot: SlotSummary }[];
  noCover: RoomWithAvailability[];
}

function halfState(
  half: HalfDay,
  date: string,
  day: Record<string, Record<HalfDay, SlotSummary>>,
  rooms: RoomWithAvailability[],
  externals: ExternalBooking[]
): HalfState {
  const state: HalfState = { bookable: [], noCover: [] };
  for (const room of rooms) {
    const slot = day[room.roomId]?.[half];
    if (!slot || slot.roomBooked || externallyBooked(externals, room.roomId, date, half)) continue;
    if (slot.free > 0) state.bookable.push({ room, slot });
    else state.noCover.push(room);
  }
  return state;
}

function tooltip(half: HalfDay, state: HalfState): string {
  const lines = [`${HALF_LABEL[half]}`];
  state.bookable.forEach(({ room, slot }) => {
    const names = slot.names?.length ? `: ${slot.names.join(', ')}` : '';
    lines.push(`${room.name}: bookable (${slot.free} free${names})`);
  });
  state.noCover.forEach(room => lines.push(`${room.name}: room free, no facilitator`));
  if (lines.length === 1) lines.push('All studios booked');
  return lines.join('\n');
}

interface Props {
  date: string;
  day: Record<string, Record<HalfDay, SlotSummary>> | undefined;
  rooms: RoomWithAvailability[];          // studios to evaluate (already room-filtered, homed only)
  externalBookings: ExternalBooking[];
  singleRoom: boolean;
}

export function CoverStrip({ date, day, rooms, externalBookings, singleRoom }: Props) {
  if (!day || !rooms.length) return null;

  return (
    <div className="grid grid-cols-2 gap-0.5 mb-1">
      {(['morning', 'afternoon'] as HalfDay[]).map(half => {
        const state = halfState(half, date, day, rooms, externalBookings);
        const title = tooltip(half, state);
        if (state.bookable.length) {
          const label = singleRoom ? 'Bookable' : `${state.bookable.length} bookable`;
          return (
            <span key={half} title={title} className="truncate text-[10px] leading-4 px-1.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
              {HALF_LABEL[half]} · {label}
            </span>
          );
        }
        if (state.noCover.length) {
          return (
            <span key={half} title={title} style={NO_COVER_STYLE} className="truncate text-[10px] leading-4 px-1.5 rounded text-red-700 border border-red-200 font-semibold">
              {HALF_LABEL[half]} · No cover
            </span>
          );
        }
        return <span key={half} title={title} />;
      })}
    </div>
  );
}
